import {
  SimpleGrid,
  Box,
  VStack,
  Flex,
  Text,
  Heading,
  Icon,
  Tooltip,
  Skeleton,
  Button,
  useBreakpointValue,
  HStack
} from '@chakra-ui/react';
import {
  Buildings,
  ChatCircle, 
  Star, 
  ArrowUp, 
  ArrowDown,
  Plus,
  FileText 
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

export default function DashboardStats({ stats, isLoading }) {
  const navigate = useNavigate();
  
  // Responsive values
  const isMobile = useBreakpointValue({ base: true, md: false });
  const columns = useBreakpointValue({ base: 1, sm: 2, md: 4 });
  const cardPadding = useBreakpointValue({ base: 4, md: 5 });
  const iconSize = useBreakpointValue({ base: "32px", md: "40px" });
  const headingSize = useBreakpointValue({ base: "lg", md: "xl" });

  // Example trend data - in a real app, this would come from the backend
  const trends = {
    sites: { change: 2, up: true },
    comments: { change: 5, up: true },
    reputation: { change: 10, up: true }
  };

  const statCards = [
    {
      title: 'Sites Submitted',
      value: stats.submittedSites,
      icon: Buildings,
      color: 'ancient.terracotta',
      darkColor: 'ancient.terracotta',
      tooltip: 'Total number of megalithic sites you have documented',
      // trend: trends.sites, // Removed trend
      action: {
        label: 'Add Site',
        onClick: () => navigate('/create-site'),
        icon: Plus
      }
    },
    {
      title: 'Total Comments',
      value: stats.comments,
      icon: ChatCircle,
      color: 'ancient.stone',
      darkColor: 'ancient.stone',
      tooltip: 'Number of comments you have made across all sites',
      // trend: trends.comments // Removed trend
    },
    {
      title: 'Reputation Points',
      value: stats.reputation,
      icon: Star,
      color: 'ancient.gold',
      darkColor: 'ancient.gold',
      tooltip: stats.reputationBreakdown || 'Points earned from site submissions and community contributions', // Use dynamic tooltip
      // trend: trends.reputation // Removed trend
    },
    {
      title: 'Draft Sites',
      value: stats.drafts,
      icon: FileText,
      color: 'ancient.stone',
      darkColor: 'ancient.stone',
      tooltip: 'Your work-in-progress site drafts',
      action: {
        label: 'Manage Drafts',
        onClick: () => navigate('/drafts'),
        icon: FileText
      }
    }
  ];

  if (isLoading) {
    return (
      <SimpleGrid columns={columns} spacing={6}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton
            key={i}
            height={isMobile ? "120px" : "140px"}
            borderRadius="md" // Match card border radius
            startColor="bg.surface" // Use theme colors for skeleton
            endColor="bg.accent"
          />
        ))}
      </SimpleGrid>
    );
  }

  return (
    <SimpleGrid columns={columns} spacing={6}>
      {statCards.map((card) => (
        <Box
          role="group"
          key={card.title}
          variant="dashboardSection" // Apply the new variant for background, border, texture
          p={cardPadding}
          transition="all 0.2s"
          _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }} // Enhance hover effect
          aria-label={`${card.title}: ${card.value}`}
          // Removed explicit bg, boxShadow, borderRadius, borderWidth - handled by variant
          // Removed borderLeft/borderColor - handled by variant
        >
          {/* Background decoration removed - handled by variant's _before */}

          <VStack align="start" spacing={3} w="100%">
            {/* Header with icon */}
            <Flex justify="space-between" w="100%" align="center">
              <Flex
                w={iconSize}
                h={iconSize}
                bg="bg.accent" // Use accent background for icon container
                color={card.color} // Use the card's specific color directly (assuming it's a theme color path)
                borderRadius="md"
                justify="center"
                align="center"
              >
                <Icon as={card.icon} weight="duotone" fontSize={isMobile ? "md" : "lg"} />
              </Flex>
              
              {/* Trend indicator */}
              {card.trend && (
                <Flex
                  align="center"
                  color={card.trend.up ? 'success' : 'error'} // Use semantic success/error colors
                  fontSize="sm"
                  fontWeight="medium"
                >
                  <Icon
                    as={card.trend.up ? ArrowUp : ArrowDown}
                    weight="bold"
                    mr={1}
                  />
                  <Text>{card.trend.change}</Text>
                </Flex>
              )}
            </Flex>

            {/* Content */}
            <VStack align="start" spacing={1} w="100%">
              <Tooltip label={card.tooltip} placement="top" bg="bg.surface" color="text.default"> {/* Themed tooltip */}
                <Text fontSize="sm" color="text.muted" fontWeight="medium"> {/* Use semantic token */}
                  {card.title}
                </Text>
              </Tooltip>
              <Flex w="100%" justify="space-between" align="center">
                <Heading size={headingSize} fontFamily="heading" color="text.default">{card.value}</Heading> {/* Use themed heading */}
                {card.action && (
                  <Button
                    size="sm"
                    color={card.color} // Use card color
                    variant="ghost" // Use ghost variant for subtle action
                    leftIcon={<Icon as={card.action.icon} weight="bold" />}
                    onClick={card.action.onClick}
                    opacity={0.7}
                    _groupHover={{ opacity: 1 }}
                    transition="opacity 0.2s"
                  >
                    {card.action.label}
                  </Button>
                )}
              </Flex>
            </VStack>
          </VStack>
        </Box>
      ))}
    </SimpleGrid>
  );
}
